import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import ExecutionStatusOverlay from './ExecutionStatusOverlay';
import { getExecutionBorderClass, type ExecutionStatus } from './executionStatus';

interface ValidateStepNodeData {
  stepName: string;
  schema?: any;
  on_error?: 'fail' | 'log' | 'sanitize' | string;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
}

function ValidateStepNode({ data, selected }: NodeProps<ValidateStepNodeData>) {
  const requiredFields = Array.isArray(data.schema?.required) ? data.schema.required : [];

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${
        selected ? 'border-amber-500 shadow-amber-200' : 'border-amber-300'
      } ${getExecutionBorderClass(data.executionStatus)}`}
    >
      <ExecutionStatusOverlay status={data.executionStatus} />
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-amber-500" />

      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          <span className="font-semibold text-sm">Validate</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-amber-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                data.onEdit?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                data.onDelete?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm text-gray-900">{data.stepName}</div>
          {data.on_error && data.on_error !== 'fail' && (
            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {data.on_error}
            </Badge>
          )}
        </div>

        {requiredFields.length > 0 && (
          <div className="text-xs text-gray-600">
            Required: <span className="font-mono">{requiredFields.join(', ')}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <Badge variant="secondary" className="text-xs">
            JSON Schema
          </Badge>
          <span className="text-xs text-gray-500">$0.000</span>
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-amber-500" />
    </div>
  );
}

export default memo(ValidateStepNode);

